import React, { useEffect } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Row,
	Col,
	Button,
} from 'reactstrap';
import { useFormikContext } from 'formik';
import { ItemsSectionInputsEPP } from './epp/ItemsSectionInputsEPP';
import { ItemsSelectorTransferencia } from '../workflows/epp/ItemsSelectorTransferencia';
import { TransferenciaSelectorTable } from '../workflows/epp/TransferenciaSelectorTable';
import OInput from '../../../../Components/OElements/OInput';

export const ItemsSectionModal = ({
	isOpen,
	formItem,
	onSetItem,
	onChange,
	onClose,
	onAddAndContinue,
	onAddAndClose,
	onAddMultiple,
}) => {
	const validation = useFormikContext();

	const [selectedTransferItems, setSelectedTransferItems] = React.useState([]);

	const isEPP = validation.values?.guide_category === 'epp';
	const operationType = validation.values?.epp_operation_type;

	useEffect(() => {
		if (formItem.PrcItem && formItem.QtyItem) {
			onSetItem('MontoItem', formItem.PrcItem * formItem.QtyItem);
		}
	}, [formItem.PrcItem, formItem.QtyItem]);

	const handleTransferItemsSelected = items => {
		setSelectedTransferItems(items);
	};

	/**
	 * Handler común para prefill de items desde selectores EPP
	 */
	const handlePrefillBase = ({
		NmbItem,
		UnmdItem,
		qtyMax,
		VlrCodigo,
		TpoCodigo,
		PrcItem,
		_module_vars,
	}) => {
		let readOnlyKeys = [];

		if (NmbItem) {
			onSetItem('NmbItem', NmbItem);
			readOnlyKeys.push('NmbItem');
		}
		if (VlrCodigo) {
			onSetItem('VlrCodigo', VlrCodigo);
			readOnlyKeys.push('VlrCodigo');
		}
		if (TpoCodigo) {
			onSetItem('TpoCodigo', TpoCodigo);
			readOnlyKeys.push('TpoCodigo');
		}
		if (PrcItem) {
			onSetItem('PrcItem', PrcItem);
			readOnlyKeys.push('PrcItem');
			readOnlyKeys.push('MontoItem');
		}
		if (UnmdItem) {
			onSetItem('UnmdItem', UnmdItem);
			readOnlyKeys.push('UnmdItem');
		}

		// Almacenar metadata del workflow
		if (_module_vars) {
			onSetItem('_module_vars', _module_vars);
		} else if (qtyMax) {
			onSetItem('_module_vars', {
				epp_qty_max: qtyMax,
			});
		}

		onSetItem('_readOnlyKeys', readOnlyKeys);
	};

	/**
	 * Renderiza el selector de items apropiado según el tipo de operación EPP
	 * Nota: El tipo 'retiro' usa un modal separado (RetiroEPPSelectorModal),
	 * por lo que este modal clásico solo maneja 'transferencia' y fallback.
	 */
	const renderEPPItemSelector = () => {
		if (!isEPP) return null;

		// Retiro EPP usa un modal separado, no debería llegar aquí
		if (operationType === 'retiro') {
			return null;
		}

		if (operationType === 'transferencia') {
			return (
				<Col lg={12}>
					<TransferenciaSelectorTable
						onItemsSelected={handleTransferItemsSelected}
					/>
					<hr style={{ marginBottom: '0px' }} />
				</Col>
			);
		}

		// Fallback al selector original si no hay tipo de operación
		return (
			<Col lg={12}>
				<ItemsSectionInputsEPP onPrefillBase={handlePrefillBase} />
				<hr style={{ marginBottom: '0px' }} />
			</Col>
		);
	};

	const isTransferencia = isEPP && operationType === 'transferencia';

	return (
		<Modal
			size={isTransferencia ? 'xl' : 'lg'}
			isOpen={isOpen}
			toggle={onClose}
			centered
		>
			<ModalHeader toggle={onClose}>Agregar ítem</ModalHeader>
			<ModalBody>
				<Row className='g-3'>
					{renderEPPItemSelector()}
					{!isTransferencia && (
						<>
							<Col lg={3}>
								<OInput
									id='QtyItem'
									name='QtyItem'
									label='Cantidad'
									type='number'
									min='0'
									step='1'
									max={formItem._module_vars?.epp_qty_max || undefined}
									value={formItem.QtyItem}
									onChange={onChange}
									validation={validation}
									disabled={formItem._readOnlyKeys.includes('QtyItem')}
									readOnly={formItem._readOnlyKeys.includes('QtyItem')}
								/>
							</Col>
							<Col lg={6}>
								<OInput
									id='NmbItem'
									name='NmbItem'
									type='text'
									label='Nombre del ítem'
									value={formItem.NmbItem}
									onChange={onChange}
									placeholder='Ej: Pallets de madera'
									validation={validation}
									disabled={formItem._readOnlyKeys.includes('NmbItem')}
									readOnly={formItem._readOnlyKeys.includes('NmbItem')}
								/>
							</Col>
							<Col lg={3}>
								<OInput
									id='UnmdItem'
									name='UnmdItem'
									type='text'
									label='Unidad'
									value={formItem.UnmdItem}
									onChange={onChange}
									placeholder='Ej: UNI, KG, LT'
									validation={validation}
									disabled={formItem._readOnlyKeys.includes('UnmdItem')}
									readOnly={formItem._readOnlyKeys.includes('UnmdItem')}
								/>
							</Col>
							<Col lg={4}>
								<OInput
									id='VlrCodigo'
									name='VlrCodigo'
									type='text'
									label='Código'
									value={formItem.VlrCodigo}
									onChange={onChange}
									placeholder='Ej: 1234567890'
									validation={validation}
									disabled={formItem._readOnlyKeys.includes('VlrCodigo')}
									readOnly={formItem._readOnlyKeys.includes('VlrCodigo')}
								/>
							</Col>
							<Col lg={4}>
								<OInput
									id='PrcItem'
									name='PrcItem'
									type='text'
									label='Precio Unitario'
									value={formItem.PrcItem}
									onChange={onChange}
									placeholder='Ej: 10'
									validation={validation}
									disabled={formItem._readOnlyKeys.includes('PrcItem')}
									readOnly={formItem._readOnlyKeys.includes('PrcItem')}
								/>
							</Col>
							<Col lg={4}>
								<OInput
									id='MontoItem'
									name='MontoItem'
									label='Monto'
									type='number'
									min='0'
									step='0.01'
									value={formItem.MontoItem}
									onChange={onChange}
									validation={validation}
									disabled={formItem._readOnlyKeys.includes('MontoItem')}
									readOnly={formItem._readOnlyKeys.includes('MontoItem')}
								/>
							</Col>
							<Col lg={12}>
								<OInput
									id='DscItem'
									name='DscItem'
									label='Descripción'
									type='textarea'
									rows={2}
									value={formItem.DscItem}
									onChange={onChange}
									validation={validation}
								/>
							</Col>
						</>
					)}
				</Row>
			</ModalBody>
			<ModalFooter>
				<Button color='secondary' onClick={onClose}>
					Cerrar
				</Button>
				{isTransferencia ? (
					<Button
						color='success'
						disabled={selectedTransferItems.length === 0}
						onClick={() => onAddMultiple(selectedTransferItems)}
					>
						<i className='ri-check-line me-1'></i>
						Confirmar selección ({selectedTransferItems.length})
					</Button>
				) : (
					<>
						<Button color='primary' onClick={onAddAndContinue}>
							<i className='ri-add-circle-line me-1'></i>
							Agregar y continuar
						</Button>
						<Button color='success' onClick={onAddAndClose}>
							<i className='ri-check-line me-1'></i>
							Agregar y cerrar
						</Button>
					</>
				)}
			</ModalFooter>
		</Modal>
	);
};
